import { ErrorModal, TreeNode, TreePickerControlV2 } from "@dexteel/mesf-core";
import { CircularProgress, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Location } from "../models/Location";
import { getLocations } from "../repositories/LocationRepository";
import { useHookLocations } from "./hooks/LocationTreePicker";

type useLocationsProps = {
	onError: (error: Error) => void;
	locationId?: number;
};

const useLocations = ({ onError, locationId }: useLocationsProps) => {
	return useQuery<Location[], Error>({
		queryKey: ["locations", locationId],
		queryFn: ({ signal }) => getLocations({ locationId }, signal),
		onError: onError,
	});
};

type Props = {
	label: string;
	value: number | null;
	onChange?: Function;
	disabled?: boolean;
	margin?: boolean;
};

export const LocationTreeSelector = ({
	label,
	value,
	onChange = () => {},
	disabled = false,
	margin = false,
}: Props) => {
	const [error, setError] = useState("");
	const [locationsTree, setLocationsTree] = useState<TreeNode>();

	const { data: locations, isLoading } = useLocations({
		onError: (error) => setError(error.message),
	});

	const { loadFilterLocations } = useHookLocations({
		locations: locations,
		setLocationsTree: setLocationsTree,
	});

	useEffect(() => {
		if (locations && locations?.length >= 0) loadFilterLocations();
	}, [locations]);

	return (
		<>
			{isLoading || !locationsTree ? (
				<TextField
					label={label}
					disabled
					variant="outlined"
					fullWidth
					size="small"
					InputProps={{
						endAdornment: <CircularProgress color="inherit" size={20} />,
					}}
					InputLabelProps={{
						style: { color: "rgb(0, 0, 0, 0.6" },
					}}
				/>
			) : (
				<div
					style={{ marginTop: margin ? -8 : 0 }}
					data-test-id="location-tree-picker"
				>
					<TreePickerControlV2
						inputTitle={label}
						title="Location"
						dataSource={locationsTree!}
						value={value || 0}
						onSelect={(value) => onChange(value)}
						selectBranch={true}
						selectActive={false}
						selectInternal={false}
						disabled={disabled}
						showClearButton
						allowSearch
						mostUsedCount={7}
					/>
				</div>
			)}
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error loading locations"
			/>
		</>
	);
};
